/**
 * InputOTP — six-digit code input matching the prototype's S02 layout.
 *
 * Built on the `input-otp` library, which gives us a single hidden text input
 * (so paste, autofill, autocomplete='one-time-code' all work) plus accessible
 * per-slot rendering. We expose three components — `InputOTP`, `InputOTPGroup`,
 * `InputOTPSlot` — matching shadcn's API so the route code reads identically.
 *
 * @see prototype/onboarding/otp.html — six 46px slots, ink-50 fill, sky focus
 */
import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { cn } from "../lib/cn.js";

export const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(function InputOTP({ className, containerClassName, ...props }, ref) {
  return (
    <OTPInput
      ref={ref}
      containerClassName={cn(
        "flex items-center gap-2 has-[:disabled]:opacity-50",
        containerClassName,
      )}
      className={cn("disabled:cursor-not-allowed", className)}
      {...props}
    />
  );
});

export const InputOTPGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function InputOTPGroup({ className, ...props }, ref) {
    return <div ref={ref} className={cn("grid grid-cols-6 gap-2 w-full", className)} {...props} />;
  },
);

export interface InputOTPSlotProps extends React.HTMLAttributes<HTMLDivElement> {
  index: number;
}

export const InputOTPSlot = React.forwardRef<HTMLDivElement, InputOTPSlotProps>(
  function InputOTPSlot({ index, className, ...props }, ref) {
    const ctx = React.useContext(OTPInputContext);
    const slot = ctx?.slots[index];
    const char = slot?.char;
    const hasFakeCaret = slot?.hasFakeCaret;
    const isActive = slot?.isActive;

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex h-[46px] w-full items-center justify-center rounded-sm",
          "border border-line bg-bg text-lg font-medium text-ink",
          "font-mono tabular-nums caret-accent transition-colors",
          "hover:border-line-2",
          isActive && "bg-paper border-accent ring-2 ring-accent-subtle",
          char && "bg-paper",
          className,
        )}
        {...props}
      >
        {char}
        {hasFakeCaret ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="h-5 w-px animate-caret-blink bg-ink" />
          </div>
        ) : null}
      </div>
    );
  },
);
